import React from 'react';
    import { Link } from 'react-router-dom';
    import { motion } from 'framer-motion';
    import * as FiIcons from 'react-icons/fi';
    import SafeIcon from '../common/SafeIcon';

    const { FiHeart, FiUsers, FiTarget, FiTrendingUp, FiArrowRight, FiCheckCircle, FiSun } = FiIcons;

    function Home() {
      const principles = [
        { icon: FiHeart, title: "Modet att vara människa", description: "Transparens och sårbarhet som styrka", color: "from-pink-500 to-rose-500" },
        { icon: FiUsers, title: "Kraften i gemenskap", description: "Tillit, samarbete och delad framgång", color: "from-blue-500 to-indigo-500" },
        { icon: FiTarget, title: "Balansens konst", description: "Energi, återhämtning och personliga gränser", color: "from-green-500 to-emerald-500" },
        { icon: FiSun, title: "Din plats, dina spelregler", description: "Autonomi, fokus och individualitet", color: "from-amber-500 to-orange-500" }
      ];

      const benefits = [
        "Ökad psykologisk trygghet i teamet",
        "Starkare tillit mellan kollegor",
        "Mer innovation och kreativitet",
        "Högre effektivitet och välmående",
        "Minskad stress och utbrändhet",
        "Bättre kommunikation och samarbete"
      ];

      return (
        <div>
          {/* Hero Section */}
          <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-transparent to-green-600/10"></div>
            <div className="relative max-w-7xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
              >
                <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                  Det är <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-green-600">okej</span> att vara <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-blue-600">människa</span> på jobbet
                </h1>
                <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto leading-relaxed">
                  Upptäck kraften i att bygga en arbetsplats där transparens, gemenskap och balans skapar psykologisk trygghet, innovation och välmående för alla.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/principer"
                    className="inline-flex items-center px-8 py-3 bg-gradient-to-r from-blue-600 to-green-600 text-white font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Utforska principerna
                    <SafeIcon icon={FiArrowRight} className="ml-2" />
                  </Link>
                  <Link
                    to="/om"
                    className="inline-flex items-center px-8 py-3 border-2 border-blue-600 text-blue-600 font-semibold rounded-full hover:bg-blue-50 transition-all duration-200"
                  >
                    Läs mer om kulturen
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Principles Grid */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white/50">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="text-center mb-16"
              >
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Fyra grundpelare för en human arbetsplats
                </h2>
                <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                  Varje princip bygger på forskning och praktisk erfarenhet för att skapa en miljö där människor kan blomstra och prestera på sitt bästa.
                </p>
              </motion.div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                {principles.map((principle, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                  >
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-br ${principle.color} flex items-center justify-center mb-4`}>
                      <SafeIcon icon={principle.icon} className="text-white text-2xl" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {principle.title}
                    </h3>
                    <p className="text-gray-600 text-sm leading-relaxed">
                      {principle.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* Benefits Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8">
            <div className="max-w-7xl mx-auto">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                    Varför Okej-kulturen fungerar
                  </h2>
                  <p style={{ color: 'rgb(75, 85, 99)' }} className="text-lg text-gray-600 mb-8">När vi skapar en miljö där det är okej att vara mänsklig, händer något magiskt. Teams blir starkare, innovation blomstrar och välmåendet ökar. Självklart är tydliga förväntningar och ramar avgörande för alla roller men "den inre människan" bör också få sitt utrymme för att vi ska kunna må bra och prestera på topp.</p>
                  <div className="space-y-4">
                    {benefits.map((benefit, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4, delay: index * 0.1 }}
                        className="flex items-center space-x-3"
                      >
                        <SafeIcon icon={FiCheckCircle} className="text-green-500 text-xl flex-shrink-0" />
                        <span className="text-gray-700">{benefit}</span>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  <div className="bg-white rounded-2xl p-6 shadow-lg">
                    <img
                      src="https://quest-media-storage-bucket.s3.us-east-2.amazonaws.com/1759405989812-Ska%C3%8C%C2%88rmavbild%202025-10-02%20kl.%2011.36.31.png"
                      alt="It's OK To poster"
                      className="w-full rounded-lg shadow-md mb-4"
                    />
                    <p className="text-sm text-gray-600 italic text-center">
                      En lista med självklarheter som tyvärr inte alltid är självklara. Bra påminnelser för en trygg arbetsplats från Government Digital Service.
                    </p>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-blue-600 to-green-600">
            <div className="max-w-4xl mx-auto text-center">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                  Redo att transformera din arbetsplats?
                </h2>
                <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
                  Börja din resa mot en mer human, effektiv och innovativ arbetsplats idag. Varje litet steg räknas.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/principer"
                    className="inline-flex items-center px-8 py-3 bg-white text-blue-600 font-semibold rounded-full hover:shadow-lg transform hover:scale-105 transition-all duration-200"
                  >
                    Kom igång nu
                    <SafeIcon icon={FiArrowRight} className="ml-2" />
                  </Link>
                  <Link
                    to="/resurser"
                    className="inline-flex items-center px-8 py-3 border-2 border-white text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-200"
                  >
                    Ladda ner resurser
                  </Link>
                </div>
              </motion.div>
            </div>
          </section>
        </div>
      );
    }

    export default Home;